import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import GoldPriceTable from '@/components/feature/GoldPriceTable';
import PriceChart from '@/components/feature/PriceChart';
import CandlestickChart from '@/components/feature/CandlestickChart';
import { candlestick30d } from '@/mocks/goldPrices';
import PriceChange from '@/components/base/PriceChange';
import { useVnGoldPrices } from '@/hooks/useVnGoldPrices';

const categories = ['Tất cả', 'Vàng miếng', 'Vàng nhẫn', 'Vàng 24K', 'Vàng 18K'];

export default function Market() {
  const [active, setActive] = useState('Tất cả');
  const [search, setSearch] = useState('');
  const { data: goldData, loading: goldLoading } = useVnGoldPrices();

  const filtered = goldData.filter((g) => {
    const matchCat = active === 'Tất cả'
      ? true
      : g.name.includes(active.replace('Vàng ', ''))
        || (active === 'Vàng miếng' && g.name.includes('miếng'))
        || (active === 'Vàng nhẫn' && (g.name.includes('nhẫn') || g.name.includes('Nhẫn')))
        || (active === 'Vàng 24K' && g.name.includes('24K'))
        || (active === 'Vàng 18K' && g.name.includes('18K'));
    const matchSearch = g.name.toLowerCase().includes(search.toLowerCase()) || g.type_code.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const xau = goldData.find((g) => g.type_code === 'XAUUSD');
  const sjc = goldData.find((g) => g.type_code === 'SJL1L10');
  const doji = goldData.find((g) => g.type_code === 'DOHNL');

  const lastUpdated = goldData.length > 0
    ? new Date(Math.max(...goldData.map((g) => new Date(g.updated_at).getTime())))
    : null;

  const todayStr = new Date().toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="border-b border-background-200/60 bg-background-100">
          <div className="mx-auto max-w-6xl px-4 md:px-6 py-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">Thị trường vàng</h1>
                <p className="mt-2 text-foreground-600">Cập nhật giá vàng trong nước theo thời gian thực · {todayStr}</p>
              </div>
              <Link
                to="/lich-su-gia-vang"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-line-chart-line"></i>
                Xem lịch sử giá
              </Link>
            </div>
          </div>
        </div>

        {/* === Giá vàng nổi bật === */}
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {goldLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-background-200/60 bg-background-100 p-5 animate-pulse">
                  <div className="h-3 bg-background-200 rounded w-24 mb-4"></div>
                  <div className="h-7 bg-background-200 rounded w-40 mb-2"></div>
                  <div className="h-4 bg-background-200 rounded w-24"></div>
                </div>
              ))
            ) : (
              <>
                {xau && (
                  <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-md bg-accent-500/15 flex items-center justify-center">
                        <i className="ri-global-line text-accent-400 text-base"></i>
                      </div>
                      <span className="text-sm font-semibold text-foreground-950">Vàng thế giới XAU/USD</span>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
                      ${Number(xau.sell || xau.buy).toLocaleString('en-US')}
                      <span className="text-base font-normal text-foreground-600"> /oz</span>
                    </p>
                    <div className="mt-2">
                      <PriceChange change={xau.change_buy} changePercent={xau.change_percent} />
                    </div>
                  </div>
                )}
                {sjc && (
                  <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-md bg-primary-500/15 flex items-center justify-center">
                        <i className="ri-copper-coin-line text-primary-400 text-base"></i>
                      </div>
                      <span className="text-sm font-semibold text-foreground-950">Vàng miếng SJC 9999</span>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
                      {Number(sjc.sell).toLocaleString('vi-VN')}đ
                      <span className="text-base font-normal text-foreground-600"> /lượng</span>
                    </p>
                    <div className="mt-2">
                      <PriceChange change={sjc.change_buy} changePercent={sjc.change_percent} />
                    </div>
                  </div>
                )}
                {doji && (
                  <div className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-md bg-secondary-500/15 flex items-center justify-center">
                        <i className="ri-store-2-line text-secondary-400 text-base"></i>
                      </div>
                      <span className="text-sm font-semibold text-foreground-950">Vàng DOJI Hà Nội</span>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
                      {Number(doji.sell).toLocaleString('vi-VN')}đ
                      <span className="text-base font-normal text-foreground-600"> /lượng</span>
                    </p>
                    <div className="mt-2">
                      <PriceChange change={doji.change_buy} changePercent={doji.change_percent} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {lastUpdated && (
            <p className="mt-3 text-xs text-foreground-500 text-right">
              Cập nhật lúc {lastUpdated.toLocaleString('vi-VN')}
            </p>
          )}
        </div>

        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/60">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    active === c ? 'bg-primary-500 text-background-950' : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm loại vàng..."
                className="w-full pl-9 pr-4 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          <GoldPriceTable />

          <div className="mt-8 rounded-xl border border-background-200/60 overflow-hidden">
            <div className="bg-[#131722] p-4 md:p-5">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-[#d1d4dc]">Biểu đồ nến giá vàng SJC</h3>
                  <p className="text-xs text-[#787b86]">Đơn vị: VNĐ / lượng · 30 ngày · Dữ liệu minh họa</p>
                </div>
                <div className="inline-flex rounded-full border border-[#2B2B43] p-1 bg-[#1e222d]">
                  <button className="px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap bg-[#2962ff] text-white cursor-pointer">
                    30 ngày
                  </button>
                </div>
              </div>
              <CandlestickChart data={candlestick30d} height={380} />
            </div>
          </div>

          <div className="mt-8">
            <PriceChart />
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {goldLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-background-200/60 bg-background-100 p-5 animate-pulse">
                  <div className="h-4 bg-background-200 rounded w-20 mb-3"></div>
                  <div className="h-3 bg-background-200 rounded w-16 mb-2"></div>
                  <div className="h-6 bg-background-200 rounded w-32"></div>
                </div>
              ))
            ) : (
              filtered.slice(0, 4).map((g) => (
                <div key={g.type_code} className="rounded-xl border border-background-200/60 bg-background-100 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-foreground-950">{g.type_code}</span>
                    <PriceChange change={g.change_buy} changePercent={g.change_percent} showPercent={false} />
                  </div>
                  <p className="text-xs text-foreground-600">Giá bán</p>
                  <p className="text-lg font-bold text-foreground-950 whitespace-nowrap">{g.sell.toLocaleString('vi-VN')}đ</p>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}